import { ZeroProvider } from "@corpuscomplete/zero-data-transformer/react";
import { useEffect, useMemo, type PropsWithChildren } from "react";
import { createZero, schema, mutators, queries } from "@corpuscomplete/core/zero";
import type { Zero } from "@rocicorp/zero";
import type { UserActor } from "@corpuscomplete/core/actor";
import { createAgentRegistry } from "@corpuscomplete/core/ai/agent-registry";
import { getRouteApi, useRouter } from "@tanstack/react-router";
import { CurrentActor } from "@corpuscomplete/core-ui/actor";
import { Rpc } from "@corpuscomplete/core-ui/rpc";
import { userLinkOptions } from "@corpuscomplete/web/rpc/client";
import { Env } from "../env";

const routeApi = getRouteApi("/_authenticated");

/**
 * Authenticated Zero bootstrap boundary.
 * Single-writer rule: this module is the only place that writes `router.context.zero`.
 */
export function ZeroInit({ children }: PropsWithChildren) {
  // The actor from routeContext is not stable, so we need to create a new one here.
  const {
    actor: { userId },
  } = routeApi.useRouteContext();
  const actor: UserActor = useMemo(() => ({ type: "user", userId }), [userId]);
  const router = useRouter();

  const zero = useMemo((): Zero => {
    const agentRegistry = createAgentRegistry();
    return createZero({
      schema,
      mutators,
      userID: actor.userId,
      cacheURL: Env.VITE_ZERO_CACHE_URL,
      context: { actor, agentRegistry },
    });
  }, [actor]);

  useEffect(() => {
    // ZeroProvider only calls `init` when it owns creating the Zero instance. We pass an already-created
    // instance here so the router context handoff has to happen in an effect instead.
    router.update({
      context: {
        ...router.options.context,
        zero,
      },
    });

    void router.invalidate();
    preload(zero);
  }, [router, zero]);

  return (
    <ZeroProvider zero={zero}>
      <Rpc.Provider linkOptions={userLinkOptions}>
        <CurrentActor.Provider actor={actor}>{children}</CurrentActor.Provider>
      </Rpc.Provider>
    </ZeroProvider>
  );
}

function preload(z: Zero) {
  z.preload(queries.foodCategory.all());
  z.preload(queries.nutrient.hasKey());
  z.preload(queries.units());
  z.preload(queries.drafts());
  z.preload(queries.bodyMetrics());
  z.preload(queries.foodCollection.forContentManagement());
  z.preload(queries.listCollection.myDefault());
  z.preload(queries.accessGrant.myGrants());
  z.preload(queries.space.byMembership());
}
